import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import InputBusca from 'src/componentes/input-busca';
import Select2 from 'src/componentes/select2';
import PerguntasEvolucao from './PerguntasEvolucao';
import PerguntaCard from './perguntas-dashboard/PerguntaCard';
import SecaoColapsavel from './perguntas-dashboard/SecaoColapsavel';
import {
  compareQuestionsByFormOrder,
  EMPTY_STATE_CLASS_NAME,
  normalizeSearchText,
  obterPerguntasComOpcoes,
  organizarPerguntasPorSecao,
  SORT_FORM_ORDER,
  SORT_HIGHEST_PERCENTAGE,
  SORT_HIGHEST_QUANTITY,
  toNumber,
} from './perguntas-dashboard/utils';

const FILTER_ALL_SECTIONS = 'todas';

const VIEW_RESPONSES = 'respostas';

const VIEW_EVOLUTION = 'evolucao';

function PerguntasDashboard({
  dados,
  entidadeId,
  filtroEvolucao,
  filtros,
  recorrente = false,
  refreshToken,
  semRespostas = false,
}) {
  const intl = useIntl();
  const perguntas = useMemo(() => obterPerguntasComOpcoes(dados), [dados]);
  const [activeView, setActiveView] = useState(VIEW_RESPONSES);
  const [busca, setBusca] = useState('');
  const [ordenacao, setOrdenacao] = useState(SORT_HIGHEST_PERCENTAGE);
  const [secaoSelecionada, setSecaoSelecionada] = useState(FILTER_ALL_SECTIONS);

  const secoes = useMemo(() => {
    const secoesUnicas = new Map();

    perguntas.forEach((pergunta) => {
      if (pergunta.secao) {
        secoesUnicas.set(String(pergunta.secao.id), pergunta.secao);
      }
    });

    return [...secoesUnicas.values()].sort(
      (a, b) => toNumber(a.ordem) - toNumber(b.ordem)
    );
  }, [perguntas]);

  const opcoesOrdenacao = useMemo(
    () => [
      {
        id: SORT_HIGHEST_PERCENTAGE,
        descricao: intl.formatMessage({ id: 'labelMaiorPercentual' }),
      },
      {
        id: SORT_HIGHEST_QUANTITY,
        descricao: intl.formatMessage({ id: 'labelMaiorQuantidade' }),
      },
      {
        id: SORT_FORM_ORDER,
        descricao: intl.formatMessage({ id: 'labelOrdemFormulario' }),
      },
    ],
    [intl]
  );

  const opcoesSecao = useMemo(
    () => [
      {
        id: FILTER_ALL_SECTIONS,
        descricao: intl.formatMessage({ id: 'labelTodasSecoes' }),
      },
      ...secoes.map((secao) => ({
        id: String(secao.id),
        descricao: secao.titulo,
      })),
    ],
    [intl, secoes]
  );

  const perguntasFiltradas = useMemo(() => {
    const termo = normalizeSearchText(busca);

    return perguntas
      .filter((pergunta) => {
        if (
          secaoSelecionada !== FILTER_ALL_SECTIONS &&
          String(pergunta.secao?.id) !== secaoSelecionada
        ) {
          return false;
        }

        if (!termo) return true;

        return (
          normalizeSearchText(pergunta.titulo).includes(termo) ||
          (pergunta.opcoes || []).some((opcao) =>
            normalizeSearchText(opcao.texto).includes(termo)
          ) ||
          (pergunta.linhas || []).some(
            (linha) =>
              normalizeSearchText(linha.texto).includes(termo) ||
              (linha.colunas || []).some((coluna) =>
                normalizeSearchText(coluna.texto).includes(termo)
              )
          )
        );
      })
      .sort(compareQuestionsByFormOrder);
  }, [busca, perguntas, secaoSelecionada]);

  const grupos = organizarPerguntasPorSecao(perguntasFiltradas);

  return (
    <div>
      {recorrente && (
        <div
          aria-label={intl.formatMessage({
            id: 'labelVisualizacaoDashboardPerguntas',
          })}
          className="tw:mb-4 tw:inline-flex tw:rounded-[7px] tw:border tw:border-solid tw:border-[#e4e7ec] tw:bg-[#f2f4f7] tw:p-0.5"
          role="group"
        >
          {[
            { id: VIEW_RESPONSES, labelId: 'labelRespostas' },
            { id: VIEW_EVOLUTION, labelId: 'labelEvolucaoAplicacoes' },
          ].map((view) => (
            <button
              aria-pressed={activeView === view.id}
              className={`tw:cursor-pointer tw:rounded-[5px] tw:border-0 tw:px-3 tw:py-1.25 tw:text-xs tw:leading-5 tw:focus:outline-none tw:focus:ring-2 tw:focus:ring-[#1d7789]/30 ${
                activeView === view.id
                  ? 'tw:bg-white tw:font-semibold tw:text-[#1d7789] tw:shadow-[0_1px_3px_rgba(16,24,40,0.14)]'
                  : 'tw:bg-transparent tw:text-[#475467]'
              }`}
              key={view.id}
              onClick={() => setActiveView(view.id)}
              type="button"
            >
              {intl.formatMessage({ id: view.labelId })}
            </button>
          ))}
        </div>
      )}
      {activeView === VIEW_EVOLUTION && recorrente ? (
        <PerguntasEvolucao
          entidadeId={entidadeId}
          filtroEvolucao={filtroEvolucao}
          filtros={filtros}
          perguntasAtuais={perguntas}
          refreshToken={refreshToken}
        />
      ) : semRespostas ? (
        <div className={EMPTY_STATE_CLASS_NAME}>
          {intl.formatMessage({ id: 'labelNenhumaRespostaParaExibir' })}
        </div>
      ) : (
        <>
          <div className="tw:mb-4.5">
            <h2 className="tw:m-0 tw:text-lg tw:leading-6 tw:font-bold tw:text-[#202124]">
              {intl.formatMessage({ id: 'labelResultadosPorPergunta' })}
            </h2>
            <p className="tw:mt-1 tw:mb-0 tw:text-[13px] tw:leading-4.75 tw:text-[#667085]">
              {intl.formatMessage({
                id: 'labelResultadosPorPerguntaDescricao',
              })}
            </p>
          </div>

          <div className="tw:mb-4.5 tw:grid tw:grid-cols-[minmax(260px,1.7fr)_minmax(180px,1fr)_minmax(180px,1fr)] tw:gap-3 tw:max-[900px]:grid-cols-1 tw:[&_.form-group]:mb-0! tw:[&_[class*='dashboard-filter'][class*='control']]:min-h-10.5!">
            <InputBusca
              aria-label={intl.formatMessage({
                id: 'labelBuscarPerguntaAlternativa',
              })}
              onChange={(event) => setBusca(event.target.value)}
              placeholder={intl.formatMessage({
                id: 'labelBuscarPerguntaAlternativa',
              })}
              value={busca}
            />

            <Select2
              aria-label={intl.formatMessage({ id: 'labelOrdenarPerguntas' })}
              clearable={false}
              classNamePrefix="dashboard-filter"
              labelKey="descricao"
              model={{
                value: opcoesOrdenacao.find((opcao) => opcao.id === ordenacao),
                requestChange: (opcao) => setOrdenacao(opcao.id),
              }}
              options={opcoesOrdenacao}
              showLabel={false}
              valueKey="id"
            />

            <Select2
              aria-label={intl.formatMessage({ id: 'labelFiltrarPorSecao' })}
              clearable={false}
              classNamePrefix="dashboard-filter"
              labelKey="descricao"
              model={{
                value: opcoesSecao.find(
                  (opcao) => opcao.id === secaoSelecionada
                ),
                requestChange: (opcao) => setSecaoSelecionada(opcao.id),
              }}
              options={opcoesSecao}
              showLabel={false}
              valueKey="id"
            />
          </div>

          {grupos.length === 0 && (
            <div className={EMPTY_STATE_CLASS_NAME}>
              {intl.formatMessage({
                id: 'labelNenhumaPerguntaEncontrada',
              })}
            </div>
          )}

          {grupos.map((grupo) => (
            <div className="tw:[&+&]:mt-3" key={grupo.chave}>
              {grupo.secao ? (
                <SecaoColapsavel
                  quantidadePerguntas={grupo.perguntas.length}
                  secao={grupo.secao}
                >
                  {grupo.perguntas.map((pergunta) => (
                    <PerguntaCard
                      intl={intl}
                      key={pergunta.perguntaId}
                      ordenacao={ordenacao}
                      pergunta={pergunta}
                    />
                  ))}
                </SecaoColapsavel>
              ) : (
                grupo.perguntas.map((pergunta) => (
                  <PerguntaCard
                    intl={intl}
                    key={pergunta.perguntaId}
                    ordenacao={ordenacao}
                    pergunta={pergunta}
                  />
                ))
              )}
            </div>
          ))}
        </>
      )}
    </div>
  );
}

export default PerguntasDashboard;
